import { useEffect, useMemo, useState } from 'react'
import { Box, Flex, Input, Spinner, Text, VStack } from '@chakra-ui/react'
import { LuLock } from 'react-icons/lu'
import { PageHeader } from '@minetrace/ui'
import { themeColors, brand } from '../../../theme'
import { permissionsService, type PermissionResponse } from '../../../api/services/permissions.service'
import { useHasPermission } from '../../../auth/PermissionContext'

interface ModuleGroup {
  module: string
  items: PermissionResponse[]
}

function groupByModule(items: PermissionResponse[]): ModuleGroup[] {
  const map = new Map<string, PermissionResponse[]>()
  for (const item of items) {
    const list = map.get(item.module) ?? []
    list.push(item)
    map.set(item.module, list)
  }
  return Array.from(map.entries())
    .map(([module, items]) => ({ module, items }))
    .sort((a, b) => a.module.localeCompare(b.module))
}

export default function PermissionsAdminPage() {
  const colors = themeColors
  const allowed = useHasPermission('permissions.list')

  const [items, setItems] = useState<PermissionResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!allowed) return
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: load data on mount
    setLoading(true)
    permissionsService
      .list()
      .then(list => { if (!cancelled) setItems(list) })
      .catch(() => { if (!cancelled) setError('No se pudo cargar el catálogo de permisos') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [allowed])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(p =>
      p.code.toLowerCase().includes(q) ||
      p.displayName.toLowerCase().includes(q) ||
      p.module.toLowerCase().includes(q),
    )
  }, [items, search])

  const groups = useMemo(() => groupByModule(filtered), [filtered])

  if (!allowed) {
    return (
      <Box p={8}>
        <Flex alignItems="center" gap={3} color={colors.textMuted}>
          <LuLock size={18} />
          <Text>No tiene permiso para ver el catálogo de permisos.</Text>
        </Flex>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Catálogo de permisos"
        subtitle="Listado del catálogo global de permisos. Para asignarlos a roles use la matriz de permisos por rol."
        colors={colors}
      />

      <Box mb={4} maxW="420px">
        <Input
          placeholder="Buscar por código, nombre o módulo…"
          value={search}
          onChange={e => setSearch(e.target.value.slice(0, 200))}
          maxLength={200}
        />
      </Box>

      {loading && (
        <Flex justifyContent="center" py={10}><Spinner color={brand.green} /></Flex>
      )}

      {error && !loading && (
        <Text color="red.500">{error}</Text>
      )}

      {!loading && !error && (
        <VStack gap={6} alignItems="stretch">
          {groups.map(group => (
            <Box
              key={group.module}
              border="1px solid"
              borderColor={colors.border}
              borderRadius="xl"
              bg={colors.bgPanel}
              overflow="hidden"
            >
              <Flex
                alignItems="center"
                gap={2}
                px={4}
                py={3}
                bg={colors.bgCard}
                borderBottom="1px solid"
                borderColor={colors.border}
              >
                <Text fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="0.05em" color={colors.textMuted}>
                  {group.module}
                </Text>
                <Text fontSize="xs" color={colors.textDimmed}>
                  ({group.items.length})
                </Text>
              </Flex>
              <Box>
                {group.items.map((p, idx) => (
                  <Flex
                    key={p.id}
                    alignItems="center"
                    px={4}
                    py={3}
                    borderTop={idx === 0 ? 'none' : '1px solid'}
                    borderColor={colors.border}
                    gap={4}
                  >
                    <Box flex="0 0 240px">
                      <Text fontSize="xs" fontFamily="mono" color={colors.textDimmed}>{p.code}</Text>
                      <Text fontSize="sm" fontWeight="500" color={colors.text}>{p.displayName}</Text>
                    </Box>
                    <Box flex="1">
                      <Text fontSize="xs" color={colors.textMuted}>{p.description ?? '—'}</Text>
                    </Box>
                    <Text fontSize="xs" color={colors.textDimmed} textTransform="uppercase">
                      {p.action}
                    </Text>
                  </Flex>
                ))}
              </Box>
            </Box>
          ))}
          {groups.length === 0 && (
            <Text color={colors.textMuted}>No hay permisos que coincidan con la búsqueda.</Text>
          )}
        </VStack>
      )}
    </Box>
  )
}
